import { checkIfLessonIsComplete } from 'utils/highschoolHelpers'
import { mapInstructorDataToSite } from 'utils/lms/instructorDataHelpers'
import { getInstructorDataForLessonActivityType } from 'components/HsIndividualResourceMarkComplete/helpers'

/**
 * Resolves the LMS registration and lesson completion for one high school
 * learning experience at one site, from the instructor/me GET payload.
 * @param {Object} params
 * @param {Object} params.instructorData - the `HighSchool` slice of instructor/me
 * @param {string|number} params.siteId
 * @param {string} params.activityType - the resource audience (student, educator, school leader)
 * @param {string} params.courseNodeId
 * @returns {{ registration: Object|null, isComplete: boolean, date: string|null, periodProgress: string|null }}
 */
export function getHsLessonStatus({
  instructorData,
  siteId,
  activityType,
  courseNodeId,
}) {
  const siteData = mapInstructorDataToSite(instructorData, siteId)
  const registration =
    getInstructorDataForLessonActivityType(siteData, activityType) || null

  if (!Array.isArray(registration?.lesson_nodes) || !courseNodeId) {
    return { registration, isComplete: false, date: null, periodProgress: null }
  }

  const { isComplete, date, periodProgress } = checkIfLessonIsComplete(
    registration.lesson_nodes,
    courseNodeId,
  )
  return {
    registration,
    isComplete: !!isComplete,
    date: date || null,
    periodProgress: periodProgress || null,
  }
}

/**
 * Whether the user has the admin claim at `siteId`. The program context can
 * briefly point at the previous site while a site switch is in progress, so
 * its site is only trusted when its `siteId` matches.
 * @param {Object} params
 * @param {Object} params.userContext - the high school program context
 * @param {string|number} params.siteId
 * @returns {boolean}
 */
export function isAdminAtSite({ userContext, siteId }) {
  if (siteId == null) return false
  const site = userContext?.sites?.find(
    contextSite => String(contextSite?.siteId) === String(siteId),
  )
  return !!site?.claims?.some(claim => claim?.toLowerCase?.() === 'admin')
}

/**
 * Whether a post-attempt refresh reflects the completion that was just sent.
 * Educator practice also has to match the submitted period progress.
 */
export function doesRefreshMatchAttempt({ lessonStatus, periodProgress }) {
  if (!lessonStatus?.isComplete) return false
  if (periodProgress) return lessonStatus.periodProgress === periodProgress
  return true
}
